import moment from 'moment';
import { useContext, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiEdit, BiPlus, BiTrash } from 'react-icons/bi';
import { BsEye } from 'react-icons/bs';
import { LoaderContext } from '../../../App';
import Pagination from '../../../components/Pagination/Pagination';
import CompanyDashboardService from '../../../core/service/CompanyDashboardService';
import './ESGAnalyticsSummaryCard.scss';

const ESGAnalyticsSummaryCard = ({
  t,
  summaryData,
  onNewClick,
  onDeleteSuccess,
  onCardClick,
  onViewClick,
  onEditClick,
  hideActionIcons = false,
  title = null,
  newButtonLabel = null,
  pagination = null,
}) => {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);

  if (!summaryData || !Array.isArray(summaryData) || summaryData.length === 0) {
    return null;
  }

  const handleDeleteClick = (itemId, e) => {
    e.stopPropagation();
    setDeleteItemId(itemId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (!deleteItemId) return;

    setLoaderDisplay(true);
    CompanyDashboardService.deleteAnalyticsFilter(deleteItemId)
      .then(res => {
        setLoaderDisplay(false);
        if (res.status === 200 || res.status === 204) {
          setShowDeleteModal(false);
          setDeleteItemId(null);
          if (onDeleteSuccess) onDeleteSuccess();
        }
      })
      .catch(() => {
        setLoaderDisplay(false);
        setShowDeleteModal(false);
        setDeleteItemId(null);
      });
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteItemId(null);
  };

  const getConfigRowNames = (configData) => {
    if (configData?.configRows && Array.isArray(configData.configRows)) {
      return configData.configRows.map(row => row.name).filter(Boolean);
    }
    return [];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return moment(dateString).format('MMM DD, YYYY');
  };

  const renderDeleteModal = () => (
    <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered className="ESGAnalyticsSummary_modal">
      <Modal.Header closeButton>
        <Modal.Title>{t('ESGAnalytics.deleteConfirmation.label') || 'Delete Configuration'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {t('ESGAnalytics.deleteConfirmationMessage.label')}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleDeleteCancel}>
          {t('ESGAnalytics.cancel.label') || 'Cancel'}
        </Button>
        <Button variant="danger" onClick={handleDeleteConfirm}>
          {t('ESGAnalytics.delete.label') || 'Delete'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <>
      <div className="ESGAnalyticsSummary" data-testid="esg-analytics-summary">
        {!hideActionIcons && (
          <header className="ESGAnalyticsSummary_header">
            <h2 className="ESGAnalyticsSummary_title">{title || t('UnitWise.label') || 'Unit Wise'}</h2>
            <div className="ESGAnalyticsSummary_actions">
              <Button variant="primary" onClick={onNewClick} className="ESGAnalyticsSummary_btn ESGAnalyticsSummary_btn--primary">
                <BiPlus className="ESGAnalyticsSummary_btn_icon" aria-hidden />
                {newButtonLabel || t('ESGAnalytics.new.label') || 'New'}
              </Button>
            </div>
          </header>
        )}

        <div className="ESGAnalyticsSummary_list">
          {summaryData.map((item) => {
            let configData;
            try {
              configData = JSON.parse(item.configData);
            } catch {
              return null;
            }
            if (!configData) return null;

            const configName = configData?.filterName;
            const configRowNames = getConfigRowNames(configData);

            return (
              <article
                key={item.id}
                className="ESGAnalyticsSummary_card"
                onClick={() => onCardClick && onCardClick(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCardClick && onCardClick(item);
                  }
                }}
                aria-label={configName}
              >
                <div className="ESGAnalyticsSummary_card_inner">
                  <div className="ESGAnalyticsSummary_card_top">
                    <span className="ESGAnalyticsSummary_card_category">{configData.category}</span>
                    {!hideActionIcons && (
                      <div className="ESGAnalyticsSummary_card_actions">
                        <Button
                          variant="link"
                          className="ESGAnalyticsSummary_iconBtn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewClick && onViewClick(item);
                          }}
                          title={t('ESGAnalytics.view.label') || 'View'}
                          aria-label={t('ESGAnalytics.view.label') || 'View'}
                        >
                          <BsEye size={18} aria-hidden />
                        </Button>
                        <Button
                          variant="link"
                          className="ESGAnalyticsSummary_iconBtn ESGAnalyticsSummary_iconBtn--edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditClick && onEditClick(item);
                          }}
                          title={t('ESGAnalytics.edit.label') || 'Edit'}
                          aria-label={t('ESGAnalytics.edit.label') || 'Edit'}
                        >
                          <BiEdit size={18} aria-hidden />
                        </Button>
                        <Button
                          variant="link"
                          className="ESGAnalyticsSummary_iconBtn ESGAnalyticsSummary_iconBtn--delete"
                          onClick={(e) => handleDeleteClick(item.id, e)}
                          title={t('ESGAnalytics.delete.label')}
                          aria-label={t('ESGAnalytics.delete.label')}
                        >
                          <BiTrash size={18} aria-hidden />
                        </Button>
                      </div>
                    )}
                  </div>

                  <h3 className="ESGAnalyticsSummary_card_title">{configName}</h3>

                  {configRowNames.length > 0 && (
                    <div className="ESGAnalyticsSummary_card_rows">
                      {configRowNames.map((name, idx) => (
                        <span key={idx} className="ESGAnalyticsSummary_card_rowItem">
                          {name}
                        </span>
                      ))}
                    </div>
                  )}

                  {!hideActionIcons && (
                    <div className="ESGAnalyticsSummary_card_meta">
                      <span className="ESGAnalyticsSummary_card_metaItem">
                        {t('ESGAnalytics.created.label')}: {formatDate(item.createdDate)}
                      </span>
                      <span className="ESGAnalyticsSummary_card_metaItem">
                        {t('ESGAnalytics.modified.label')}: {formatDate(item.lastModifiedDate)}
                      </span>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {pagination && pagination.itemsTotalCount > 0 && (
          <div className="ESGAnalyticsSummary_pagination p-2 List_footer_count">
            <Pagination
              itemsTotalCount={pagination.itemsTotalCount}
              itemsPerPage={pagination.itemsPerPage}
              currentPage={pagination.currentPage}
              setCurrentPage={pagination.setCurrentPage}
              setItemsPerPage={pagination.setItemsPerPage}
              alwaysShown={true}
              pageClicked={pagination.onPageChange}
            />
          </div>
        )}

        {!hideActionIcons && renderDeleteModal()}
      </div>
    </>
  );
};

export default withTranslation()(ESGAnalyticsSummaryCard);
